import { registerPasskey, requestRegistrationOptions } from '@/services/passkey';
import { arrayBufferToBase64Url, base64ToArrayBuffer } from '@/utils/base64Utils';
import { TRqPasskeyRegisterAttestation, TRsRegisterOptions } from '@/services/types/Passkey';

export class PasskeyUnsupportedError extends Error {}
export class PasskeyCreationCancelledError extends Error {}

const isPasskeySupported = () =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  Boolean(window.PublicKeyCredential) &&
  Boolean(navigator.credentials);

export const useCreatePasskey = () => {
  const requestOptions = async (): Promise<TRsRegisterOptions> => {
    const response = await requestRegistrationOptions();
    return response.data;
  };

  const createCredential = async (options: TRsRegisterOptions): Promise<PublicKeyCredential> => {
    const credentialCreationOptions = {
      publicKey: {
        challenge: base64ToArrayBuffer(options.challenge),
        rp: options.rp,
        user: {
          // options.user.id is the raw customer id string (not base64) - encode it as UTF-8
          // bytes rather than decoding it as base64, which would silently corrupt it.
          id: new TextEncoder().encode(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName
        },
        pubKeyCredParams: options.pubKeyCredParams.map((param) => ({
          type: param.type as PublicKeyCredentialType,
          alg: param.alg
        })),
        attestation: options.attestation as AttestationConveyancePreference,
        excludeCredentials: options.excludeCredentials.map((credential) => ({
          type: credential.type as PublicKeyCredentialType,
          id: base64ToArrayBuffer(credential.id)
        })),
        authenticatorSelection: {
          requireResidentKey: options.authenticatorSelection.requireResidentKey,
          residentKey: options.authenticatorSelection.residentKey as ResidentKeyRequirement,
          userVerification: options.authenticatorSelection
            .userVerification as UserVerificationRequirement
        }
      }
    } as CredentialCreationOptions;

    try {
      const credential = await navigator.credentials.create(credentialCreationOptions);
      if (!credential) {
        throw new PasskeyCreationCancelledError();
      }
      return credential as PublicKeyCredential;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        throw new PasskeyCreationCancelledError();
      }
      throw err;
    }
  };

  const buildAttestation = (
    challenge: string,
    credential: PublicKeyCredential
  ): TRqPasskeyRegisterAttestation => {
    const response = credential.response as AuthenticatorAttestationResponse;

    return {
      credentialId: credential.id,
      challenge,
      response: {
        clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
        attestationObject: arrayBufferToBase64Url(response.attestationObject),
        transports: response.getTransports ? response.getTransports() : null
      }
    };
  };

  const create = async (): Promise<void> => {
    if (!isPasskeySupported()) {
      throw new PasskeyUnsupportedError();
    }

    const options = await requestOptions();
    const credential = await createCredential(options);
    const attestation = buildAttestation(options.challenge, credential);

    await registerPasskey(attestation);
  };

  return {
    create
  };
};
